/**
 * PetPolicy — Daymark Uptown (copied from Weidner Classic)
 *
 * Daymark's Multihub pet fee/deposit/rent/max-pets fields are "0", which hides
 * the stat cards, so those value tokens default to the property's published
 * policy (2 pets, $300 deposit, $300 one-time fee, $40/mo) instead of the
 * property fields. Weight limit is N/A, so that card stays hidden.
 *
 * Standard Weidner pet-policy layout, shared with the Comfy, Homestead,
 * Belmont, Roxanne, Rise, Oasis, and Aster Weidner themes:
 *
 *   Left column  — paw "Dogs & Cats Welcome" badge, property pet-policy
 *                  description, boxed Pet Restrictions list, View Amenities CTA.
 *   Right column — 2-up stat cards: Pets Per Household, Max Weight,
 *                  Pet Deposit (Refundable), Pet Fee (Non-Refundable),
 *                  Monthly Pet Rent. Cards hide when the value is empty.
 *
 * Restrictions use `property_pet_restrictions` when it has real copy
 * (one-breed-per-line text becomes a bullet list); empty or Multihub's stock
 * "Breed restrictions apply" falls back to the standard Weidner breed list.
 *
 * Weidner Classic styling: mustard stat labels, accent rule on the
 * restrictions box, and the mustard arrow CTA used by AmenitiesList.
 * Interior hero stays on HeroSolidBG in pages.json.
 *
 * Maintenance checklist when re-syncing with the other Weidner themes:
 *   1. Keep token keys (`petpolicy.*`) aligned with the shared Weidner
 *      PetPolicy so site overrides carry across themes.
 *   2. Keep DEFAULT_PET_RESTRICTIONS_HTML and the restrictions defaultValue
 *      identical (standard 13-breed Weidner list).
 *   3. Run `pnpm run lint:schema` and `pnpm run typecheck`.
 */

import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveRichText } from '@vibesites/runtime';
import {
  usePropertyField,
  useContentToken,
  useSlotGroup,
  useVibesites,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { PetsAllowedIcon } from '@vibesites/assets';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const MUSTARD = 'var(--vibe-color-secondary)';

// Matches an empty <p> tag — empty meaning truly empty, whitespace-only,
// or containing only &nbsp; / <br> filler that some richtext editors emit.
const EMPTY_P = String.raw`<p\b[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>`;
const LEADING_EMPTY_P_RE = new RegExp(`^(?:\\s*${EMPTY_P}\\s*)+`, 'i');
const TRAILING_EMPTY_P_RE = new RegExp(`(?:\\s*${EMPTY_P}\\s*)+$`, 'i');

function stripEdgeEmptyParagraphs(html: string): string {
  return html.replace(LEADING_EMPTY_P_RE, '').replace(TRAILING_EMPTY_P_RE, '').trim();
}

/** Standard Weidner restricted-breed list — also inlined on petpolicy.restrictions.defaultValue. */
const DEFAULT_PET_RESTRICTIONS_HTML =
  '<ul><li>Pit Bull &amp; Pit Bull Mixes</li><li>American Pit Bull Terrier</li><li>American Staffordshire Terrier</li><li>Wolf Hybrids</li><li>Doberman Pinscher</li><li>Saint Bernard</li><li>American Bulldog</li><li>Staffordshire Bull Terrier</li><li>Rottweiler</li><li>German Shepherd</li><li>Chow Chow</li><li>Great Dane</li><li>Akita</li></ul>';

/**
 * Multihub often stores the stock phrase "Breed restrictions apply" instead of
 * an actual list. Treat that (and empty/near-empty HTML) as a placeholder so
 * we can show the standard breed list instead. Real custom copy is left alone.
 */
function isBreedRestrictionsPlaceholder(html: string | null | undefined): boolean {
  if (html == null) return true;
  const text = html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/g, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/[.!]+$/g, '')
    .trim();
  return text === '' || text === 'false' || text === 'breed restrictions apply';
}

/**
 * Property pet restrictions are often stored as one breed per paragraph
 * (or one breed per newline / <br>). Convert those into a real bullet list
 * so they read as bullets instead of stacked paragraphs. Leave existing
 * <ul>/<ol> markup alone.
 *
 * A trailing disclaimer (longer prose after the breed names) is kept as a
 * normal paragraph below the list — not as a bullet.
 */
function toRestrictionsBulletList(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) return '';
  if (/<(ul|ol)\b/i.test(trimmed)) return trimmed;

  const normalizeItem = (text: string) =>
    text.replace(/<br\s*\/?>/gi, ' ').replace(/\s+/g, ' ').trim();

  const isEmptyItem = (text: string) =>
    text.length === 0 || /^(?:&nbsp;|\s)*$/i.test(text);

  const stripTags = (text: string) =>
    text.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();

  /** Trailing prose note after short breed names — not another restricted breed. */
  const isDisclaimer = (item: string, priorItems: string[]): boolean => {
    const plain = stripTags(item);
    if (!plain || priorItems.length === 0) return false;

    const wordCount = plain.split(/\s+/).filter(Boolean).length;
    const priorWordCounts = priorItems
      .map((p) => stripTags(p).split(/\s+/).filter(Boolean).length)
      .filter((n) => n > 0);
    const maxPriorWords = priorWordCounts.length ? Math.max(...priorWordCounts) : 0;
    const priorLengths = priorItems.map((p) => stripTags(p).length).filter((n) => n > 0);
    const avgPrior =
      priorLengths.length > 0
        ? priorLengths.reduce((sum, n) => sum + n, 0) / priorLengths.length
        : 0;

    // Breed names are short labels; disclaimers are full sentences / notes.
    if (wordCount >= Math.max(10, maxPriorWords + 4)) return true;
    if (/[.!?]\s*$/.test(plain) && wordCount >= 6) return true;
    if (
      /\b(please|note|disclaimer|management|reserves|subject to|additional|may be|not limited|all[- ]inclusive|contact|call for|apply|aggress|regardless)\b/i.test(
        plain,
      ) &&
      wordCount >= 5
    ) {
      return true;
    }
    if (plain.length >= Math.max(72, avgPrior * 2.5) && wordCount >= 6) return true;

    return false;
  };

  const splitDisclaimer = (items: string[]): { bullets: string[]; disclaimer: string | null } => {
    if (items.length < 2) return { bullets: items, disclaimer: null };
    const last = items[items.length - 1];
    const prior = items.slice(0, -1);
    if (isDisclaimer(last, prior)) {
      return { bullets: prior, disclaimer: last };
    }
    return { bullets: items, disclaimer: null };
  };

  const renderList = (items: string[], escapePlain = false): string => {
    const { bullets, disclaimer } = splitDisclaimer(items);
    if (bullets.length < 2 && !disclaimer) {
      return items.map((item) => `<p>${item}</p>`).join('');
    }
    const escape = (s: string) =>
      s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    const format = (s: string) => (escapePlain ? escape(s) : s);
    const listHtml =
      bullets.length > 0
        ? `<ul>${bullets.map((item) => `<li>${format(item)}</li>`).join('')}</ul>`
        : '';
    const disclaimerHtml = disclaimer ? `<p>${format(disclaimer)}</p>` : '';
    return `${listHtml}${disclaimerHtml}`;
  };

  const paragraphs = [...trimmed.matchAll(/<p\b[^>]*>([\s\S]*?)<\/p>/gi)].map((match) =>
    match[1].trim(),
  );

  // Multiple <p> tags → one bullet each (trailing disclaimer peeled off)
  if (paragraphs.length >= 2) {
    const items = paragraphs.map(normalizeItem).filter((text) => !isEmptyItem(text));
    if (items.length >= 2) {
      return renderList(items);
    }
  }

  // Single <p> with <br>-separated lines → one bullet each
  if (paragraphs.length === 1 && /<br\s*\/?>/i.test(paragraphs[0])) {
    const items = paragraphs[0]
      .split(/<br\s*\/?>/i)
      .map(normalizeItem)
      .filter((text) => !isEmptyItem(text));
    if (items.length >= 2) {
      return renderList(items);
    }
  }

  // Plain text with newlines (no HTML tags)
  if (!/<[a-z][\s\S]*>/i.test(trimmed)) {
    const lines = trimmed
      .split(/\r?\n+/)
      .map((line) => line.trim())
      .filter(Boolean);
    if (lines.length >= 2) {
      return renderList(lines, true);
    }
  }

  return trimmed;
}

function hasValue(val: unknown): val is string {
  if (val === null || val === undefined) return false;
  const s = String(val).trim().toLowerCase();
  return (
    s !== '' &&
    s !== 'false' &&
    s !== 'null' &&
    s !== 'undefined' &&
    s !== '0' &&
    s !== 'none' &&
    s !== 'n/a' &&
    s !== 'na'
  );
}

function isTruthy(val: unknown): boolean {
  if (val === true) return true;
  if (!val) return false;
  const s = String(val).trim().toLowerCase();
  return s === 'true' || s === 'yes' || s === '1';
}

function formatCurrency(val: string): string {
  const num = parseFloat(val.replace(/[^0-9.]/g, ''));
  if (isNaN(num)) return val;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View Amenities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function PetPolicy() {
  const { property } = useVibesites();
  const allowsCats = usePropertyField('property_allows_cats');
  const allowsDogs = usePropertyField('property_allows_dogs');
  const policyDescription = usePropertyField('property_pet_policy_description') as string | null;
  const policyInfo = usePropertyField('property_petpolicy_info') as string | null;

  const restrictionsRaw = useContentToken('petpolicy.restrictions');
  const restrictionsResolved = restrictionsRaw
    ? toRestrictionsBulletList(
        stripEdgeEmptyParagraphs(resolveRichText(restrictionsRaw, property as Record<string, any>)),
      )
    : '';
  const restrictionsHtml = isBreedRestrictionsPlaceholder(restrictionsResolved)
    ? DEFAULT_PET_RESTRICTIONS_HTML
    : restrictionsResolved;

  const ctaLink = useSlotGroup('petpolicy.cta_link', ctaLinkDef);

  const petDepositSublabel = useContentToken('petpolicy.pet_deposit_sublabel');
  const petFeeSublabel = useContentToken('petpolicy.pet_fee_sublabel');

  const maxPetsValue = useContentToken('petpolicy.max_pets_value');
  const maxWeightValue = useContentToken('petpolicy.max_weight_value');
  const petDepositValue = useContentToken('petpolicy.pet_deposit_value');
  const petFeeValue = useContentToken('petpolicy.pet_fee_value');
  const monthlyRentValue = useContentToken('petpolicy.monthly_rent_value');

  const catsDogsList = [
    isTruthy(allowsDogs) && 'Dogs',
    isTruthy(allowsCats) && 'Cats',
  ].filter(Boolean);

  const welcomeText = useContentToken('petpolicy.welcome_text');

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const showWelcomeBadge = catsDogsList.length > 0 || Boolean(welcomeText);

  const showMaxPets = hasValue(maxPetsValue);
  const showMaxWeight = hasValue(maxWeightValue);
  const showPetDeposit = hasValue(petDepositValue);
  const showPetFee = hasValue(petFeeValue);
  const showMonthlyRent = hasValue(monthlyRentValue);
  const showStats =
    showMaxPets || showMaxWeight || showPetDeposit || showPetFee || showMonthlyRent;

  const descriptionHtml = policyDescription || policyInfo || null;

  return (
    <section ref={sectionRef} className="reveal-fade-up pet-policy">
      <div className="pet-policy__inner">
        <div className="pet-layout">
          {/* Left column: description + restrictions */}
          <div className="pet-content">
            {showWelcomeBadge && (
              <div className="pet-welcome">
                <PetsAllowedIcon size={28} color={MUSTARD} />
                <RichTextContent
                  path="petpolicy.welcome_text"
                  as="span"
                  className="pet-welcome__text"
                />
              </div>
            )}

            {descriptionHtml && (
              <RichTextContent
                path="petpolicy.description"
                label="Pet Policy Description"
                as="div"
                className="pet-description"
              />
            )}

            {hasValue(restrictionsHtml) && (
              <div className="pet-restrictions">
                <RichTextContent
                  path="petpolicy.restrictions_heading"
                  as="h3"
                  className="pet-restrictions__heading"
                />
                <EditableToken
                  path="petpolicy.restrictions"
                  type="richtext"
                  label="Pet Restrictions"
                  as="div"
                  className="pet-restrictions__body"
                >
                  <span
                    dangerouslySetInnerHTML={{ __html: restrictionsHtml }}
                    style={{ display: 'contents' }}
                  />
                </EditableToken>
              </div>
            )}

            <div className="pet-cta-wrap">
              <EditableSlotGroup groupPath="petpolicy.cta_link" definition={ctaLinkDef}>
                <a
                  href={ctaLink.url || '/amenities'}
                  target={ctaLink._target || '_self'}
                  className="pet-cta"
                >
                  <span>{ctaLink.label || 'View Amenities'}</span>
                  <svg
                    className="pet-cta__icon"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M5 12h14M13 6l6 6-6 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Right column: stat cards */}
          {showStats && (
            <div className="pet-stats">
              {showMaxPets && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.max_pets_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.max_pets_value" as="span" className="pet-stat-value">
                    {maxPetsValue ? `${maxPetsValue} Allowed` : ''}
                  </EditableToken>
                </div>
              )}
              {showMaxWeight && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.max_weight_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.max_weight_value" as="span" className="pet-stat-value">
                    {maxWeightValue ? `${maxWeightValue} lbs` : ''}
                  </EditableToken>
                </div>
              )}
              {showPetDeposit && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.pet_deposit_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.pet_deposit_value" as="span" className="pet-stat-value">
                    {petDepositValue ? formatCurrency(petDepositValue) : ''}
                  </EditableToken>
                  <EditableToken path="petpolicy.pet_deposit_sublabel" as="span" className="pet-stat-sublabel">
                    {petDepositSublabel || '(Refundable)'}
                  </EditableToken>
                </div>
              )}
              {showPetFee && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.pet_fee_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.pet_fee_value" as="span" className="pet-stat-value">
                    {petFeeValue ? formatCurrency(petFeeValue) : ''}
                  </EditableToken>
                  <EditableToken path="petpolicy.pet_fee_sublabel" as="span" className="pet-stat-sublabel">
                    {petFeeSublabel || '(Non-Refundable)'}
                  </EditableToken>
                </div>
              )}
              {showMonthlyRent && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.monthly_rent_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.monthly_rent_value" as="span" className="pet-stat-value">
                    {monthlyRentValue ? `${formatCurrency(monthlyRentValue)}/mo` : ''}
                  </EditableToken>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .pet-policy {
          padding: 3rem 0;
          background: var(--vibe-color-background, #fff);
        }
        @media (min-width: 768px) {
          .pet-policy { padding: 5rem 0; }
        }
        .pet-policy__inner {
          max-width: 64rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) {
          .pet-policy__inner { padding: 0 1.5rem; }
        }
        @media (min-width: 1024px) {
          .pet-policy__inner { padding: 0 2rem; }
        }

        .pet-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
        }
        @media (min-width: 768px) {
          .pet-layout {
            grid-template-columns: 1fr 1fr;
            gap: 3rem;
            align-items: start;
          }
        }

        .pet-content { order: 2; }
        @media (min-width: 768px) {
          .pet-content { order: 1; }
        }

        .pet-welcome {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }
        .pet-welcome__text {
          font-family: var(--vibe-font-heading);
          font-size: 1.125rem;
          font-weight: 500;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #000;
        }
        .pet-welcome__text p { margin: 0; }

        .pet-description {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.75;
          color: var(--vibe-color-text);
          margin-bottom: 1.5rem;
        }
        .pet-description p { margin: 0 0 0.75rem; }
        .pet-description ul {
          list-style: disc;
          padding-left: 1.25rem;
          margin: 0 0 0.75rem;
        }
        .pet-description li { margin-bottom: 0.25rem; }
        .pet-description a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        .pet-restrictions {
          padding: 1rem 1.25rem;
          background: #f7f7f7;
          border-left: 4px solid ${MUSTARD};
          margin-bottom: 0.5rem;
        }
        .pet-restrictions__heading {
          font-family: var(--vibe-font-heading);
          font-size: 1rem;
          font-weight: 500;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--vibe-color-text);
          margin: 0 0 0.75rem;
        }
        .pet-restrictions__heading p { margin: 0; }
        .pet-restrictions__body {
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          line-height: 1.65;
          color: var(--vibe-color-text-muted);
        }
        .pet-restrictions__body p { margin: 0 0 0.5rem; }
        .pet-restrictions__body p:empty { display: none; margin: 0; }
        .pet-restrictions__body ul {
          list-style: disc;
          padding-left: 1.25rem;
          margin: 0 0 0.5rem;
        }
        .pet-restrictions__body li { margin-bottom: 0.125rem; }
        .pet-restrictions__body a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        .pet-cta-wrap { margin-top: 2rem; }
        .pet-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.65rem;
          min-width: 14rem;
          height: 56px;
          padding: 0 1.75rem;
          background: ${MUSTARD};
          color: var(--vibe-color-text-on-secondary);
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          text-decoration: none;
          border: none;
          border-radius: 0;
          transition: background 0.25s ease, color 0.25s ease;
        }
        .pet-cta:hover {
          background: #b6b6b6;
          color: #000;
        }
        .pet-cta__icon { flex-shrink: 0; }

        .pet-stats {
          order: 1;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
        }
        @media (min-width: 768px) {
          .pet-stats { order: 2; }
        }

        .pet-stat-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 1.25rem 1rem;
          border: 1px solid #e8e8e8;
          background: #fff;
          border-radius: 2px;
          min-height: 100px;
          box-shadow: 0 4px 18px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04);
        }
        .pet-stat-label {
          font-family: var(--vibe-font-heading);
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: ${MUSTARD};
          margin-bottom: 0.5rem;
        }
        .pet-stat-label p { margin: 0; }
        .pet-stat-value {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          font-weight: 500;
          color: #4b4d51;
        }
        .pet-stat-sublabel {
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          color: var(--vibe-color-text-muted);
          opacity: 0.7;
          margin-top: 0.15rem;
        }

        @media (prefers-reduced-motion: reduce) {
          .pet-cta { transition: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PetPolicy',
  displayName: 'Pet Policy',
  tokens: {
    'petpolicy.welcome_text': {
      label: 'Pets Welcome Text',
      type: 'richtext',
      description: 'Badge text shown next to the paws icon.',
      defaultValue: '<p>Dogs &amp; Cats Welcome</p>',
    },
    'petpolicy.description': {
      label: 'Pet Policy Description',
      type: 'richtext',
      defaultField: 'property_pet_policy_description',
      suggestedFields: ['property_pet_policy_description', 'property_petpolicy_info'],
    },
    'petpolicy.restrictions_heading': {
      label: 'Pet Restrictions Heading',
      type: 'richtext',
      description: 'Heading shown above the pet restrictions list.',
      defaultValue: '<p>Pet Restrictions</p>',
    },
    'petpolicy.restrictions': {
      label: 'Pet Restrictions',
      type: 'richtext',
      defaultField: 'property_pet_restrictions',
      defaultValue:
        '<ul><li>Pit Bull &amp; Pit Bull Mixes</li><li>American Pit Bull Terrier</li><li>American Staffordshire Terrier</li><li>Wolf Hybrids</li><li>Doberman Pinscher</li><li>Saint Bernard</li><li>American Bulldog</li><li>Staffordshire Bull Terrier</li><li>Rottweiler</li><li>German Shepherd</li><li>Chow Chow</li><li>Great Dane</li><li>Akita</li></ul>',
      description:
        'Restricted breeds and other pet limits. Uses property_pet_restrictions when it has real copy; Multihub\'s "Breed restrictions apply" placeholder (or empty) shows the standard Weidner breed list.',
    },
    'petpolicy.max_pets_label': {
      label: 'Pets Per Household Label',
      type: 'richtext',
      defaultValue: '<p>Pets Per Household</p>',
    },
    'petpolicy.max_pets_value': {
      label: 'Pets Per Household Value',
      type: 'text',
      defaultValue: '2',
      suggestedFields: ['property_max_pets'],
      description: 'Number of pets allowed per household.',
    },
    'petpolicy.max_weight_label': {
      label: 'Max Weight Label',
      type: 'richtext',
      defaultValue: '<p>Max Weight</p>',
    },
    'petpolicy.max_weight_value': {
      label: 'Max Weight Value',
      type: 'text',
      defaultField: 'property_max_pet_weight_limit',
      description:
        'Maximum pet weight in lbs. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicy.pet_deposit_label': {
      label: 'Pet Deposit Label',
      type: 'richtext',
      defaultValue: '<p>Pet Deposit</p>',
    },
    'petpolicy.pet_deposit_value': {
      label: 'Pet Deposit Value',
      type: 'text',
      defaultValue: '300',
      suggestedFields: ['property_pet_deposit'],
      description: 'Pet deposit amount (number only; formatted as currency).',
    },
    'petpolicy.pet_deposit_sublabel': {
      label: 'Pet Deposit Sublabel',
      type: 'text',
      defaultValue: '(Refundable)',
    },
    'petpolicy.pet_fee_label': {
      label: 'Pet Fee Label',
      type: 'richtext',
      defaultValue: '<p>Pet Fee</p>',
    },
    'petpolicy.pet_fee_value': {
      label: 'Pet Fee Value',
      type: 'text',
      defaultValue: '300',
      suggestedFields: ['property_upfront_pet_fee'],
      description: 'One-time pet fee amount (number only; formatted as currency).',
    },
    'petpolicy.pet_fee_sublabel': {
      label: 'Pet Fee Sublabel',
      type: 'text',
      defaultValue: '(Non-Refundable)',
    },
    'petpolicy.monthly_rent_label': {
      label: 'Monthly Pet Rent Label',
      type: 'richtext',
      defaultValue: '<p>Monthly Pet Rent</p>',
    },
    'petpolicy.monthly_rent_value': {
      label: 'Monthly Pet Rent Value',
      type: 'text',
      defaultValue: '40',
      suggestedFields: ['property_monthly_pet_rent'],
      description: 'Monthly pet rent amount (number only; formatted as currency).',
    },
  },
  slotGroups: {
    'petpolicy.cta_link': {
      label: 'CTA Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'View Amenities' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
};

(PetPolicy as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default PetPolicy;
